import { Component, Injector, forwardRef, Input, EventEmitter, Output, OnInit, ElementRef, ViewChild, OnDestroy, AfterViewInit, SimpleChanges } from '@angular/core';
import { NG_VALUE_ACCESSOR, ControlContainer, FormControl } from '@angular/forms';
import { ConnectionPositionPair } from '@angular/cdk/overlay';

import { Subject } from 'rxjs';
import { tap, takeUntil } from 'rxjs/operators';

import { ArrowDirection } from '@apps/app.constants';
import { CustomFormGroup, OptionValue9 } from '@ui/components/form9/common';
import { PopupTooltipConfig } from '@ui/misc/popup-tooltip/cog/popup-tooltip-config';
import { PopupTooltipService } from '@ui/misc/popup-tooltip';

import { PopupDropdownParameters } from './popup-dropdown/popup-dropdown-parameters';
import { PopupDropdownComponent } from './popup-dropdown/popup-dropdown.component';

import { ComplexInputBase } from '../../../base/complex-input-base';
import { inputDropdown9RequiredValidator } from './input-dropdown9.validators';
import { PopupDropdownService } from './services/popup-dropdown.service';
import { UtilsService } from '@common/services';

@Component({
    selector: 'aa-input-dropdown9',
    templateUrl: './input-dropdown9.component.html',
    styleUrls: [
        '../input9.component.scss',
        './input-dropdown9.component.scss'
    ],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef((): typeof InputDropdown9Component => InputDropdown9Component),
            multi: true,
        },
        PopupDropdownService
    ],
    standalone: false
})
export class InputDropdown9Component<T> extends ComplexInputBase<T | T[]> implements OnInit, AfterViewInit, OnDestroy
{
    private static identifier: number = 0;
    @Input() items: T[];
    @Input() labelKey: keyof T;
    @Input() valueKey: keyof T;
    @Input() groupBy: (item: T) => OptionValue9;
    @Input() placeholder: string = '';
    @Input() inputDisabled: boolean;
    @Input() multiple: boolean = false;
    @Input() value?: T;
    @Input() clearable = false;
    private _inputZone: ElementRef;
    @ViewChild('inputZone') set inputZone (value: ElementRef)
    {
        if (value)
        {
            this._inputZone = value;
        }
    }
    @Output() clickedItem: EventEmitter<T> = new EventEmitter<T>();
    @Output() cleared: EventEmitter<null> = new EventEmitter();
    public formGroup: CustomFormGroup;
    public searchControl = new FormControl();
    public open: boolean = false;
    public displayValue: string = '';
    private originalOptions: T[];
    private popupDropdownComponent: PopupDropdownComponent<T>;
    private parameters: PopupDropdownParameters<T>;
    private readonly tooltipHeight: number = 150;
    private readonly tooltipPositions: ConnectionPositionPair[] = [
        {
            originX: 'start',
            overlayX: 'start',
            originY: 'bottom',
            overlayY: 'top',
            offsetY: -30
        },
        {
            originX: 'start',
            overlayX: 'start',
            originY: 'top',
            overlayY: 'bottom',
            offsetY: -2
        }
    ];
    private destroyator = new Subject();

    constructor (
        injector: Injector,
        controlContainer: ControlContainer,
        private popupTooltipService: PopupTooltipService<PopupDropdownComponent<T>, PopupDropdownParameters<T>>,
        private popupDropdownService: PopupDropdownService,
        private elementRef: ElementRef
    )
    {
        super(injector, controlContainer);

        this.fieldId = `idInputDropdown9-${InputDropdown9Component.identifier++}`;
    }

    ngOnInit (): void
    {
        this.originalOptions = [...this.items];
        this.initControl(this.required ? [inputDropdown9RequiredValidator(this.customMsg)] : null);

        this.parameters = new PopupDropdownParameters();
        this.parameters.items = this.items;
        this.parameters.labelKey = this.labelKey;

        // this.searchControl.valueChanges
        //     .pipe(
        //         debounceTime(AppConstants.FormDebounceTime),
        //         // untilDestroyed(this)
        //     )
        //     .subscribe(term => this.search(term));
    }

    ngAfterViewInit (): void
    {
        if (!UtilsService.isNullOrUndefined(this.value))
        {
            this.displayValue = this.getDisplayValue(this.value).toString();
        }
    }

    ngOnDestroy (): void
    {
        this.popupTooltipService.close();
        this.destroyator.next(null);
        this.destroyator.complete();
    }

    // private search (value: string): void
    // {
    //     this.options = this.originalOptions
    //         .filter((option: SelectOption9): boolean =>
    //             option['label'].includes(value)
    //         );
    // }

    public onArrowClick (direction: ArrowDirection): void
    {
        if (direction === ArrowDirection.OPEN)
        {
            const popupTooltipConfig = new PopupTooltipConfig(this.elementRef.nativeElement.getBoundingClientRect().width, this.tooltipHeight, this.tooltipPositions);

            this.popupDropdownComponent = this.popupTooltipService.create(this._inputZone, PopupDropdownComponent, popupTooltipConfig, this.parameters);
            this.popupDropdownComponent.groupBy = this.groupBy;
            this.popupDropdownComponent.clickedItem
                .pipe(
                    tap((clickedItem: T): void=>
                    {
                        if (this.multiple)
                        {
                            this.stateValue.push(clickedItem);
                            // this.ctrlValue = this.stateValue;
                        }
                        else
                        {
                            this.displayValue = this.getDisplayValue(clickedItem).toString();
                            this.ctrlValue = clickedItem;
                            this.clickedItem.emit(clickedItem);
                            this.popupTooltipService.close();
                        }

                        this.control.markAsDirty();
                    }),
                    takeUntil(this.destroyator)
                )
                .subscribe();
        }
        else
        {
            this.popupTooltipService.close();
        }
    }

    public onClickWrapper (): void
    {
        this.onArrowClick(ArrowDirection.OPEN);
    }

    public onClearClick (event: Event): void
    {
        event.stopPropagation();

        this.control.setValue(null);
        this.control.markAsDirty();
        this.cleared.emit();
    }

    public isClearButtonShown (): boolean
    {
        return this.clearable && !UtilsService.isNullOrEmpty(this.control.value);
    }

    // ngAfterViewChecked ()
    // {
    //     this.formGroup.get('formControlName').setValue(this.value);
    //     this.changeDetectorRef.detectChanges();
    // }

    // onSelected ()
    // {
    //     this.value = this.formGroup.get('formControlName').value;
    //     this.select.emit(this.value);
    // }
}
